export type DispositionType =
  | 'home_health'
  | 'snf_at_home'
  | 'hospital_at_home'
  | 'hospice'
  | 'infusion_at_home'
  | 'palliative';

export const DISPOSITION_LABELS: Record<DispositionType, string> = {
  home_health: 'Home Health',
  snf_at_home: 'SNF at Home',
  hospital_at_home: 'Hospital at Home',
  hospice: 'Hospice',
  infusion_at_home: 'Infusion at Home',
  palliative: 'Palliative',
};

export type ClinicalNeed =
  | 'pt_ot'
  | 'wound_care'
  | 'iv_therapy'
  | 'medication_management'
  | 'skilled_nursing'
  | 'speech_therapy'
  | 'cardiac_monitoring';

export const CLINICAL_NEED_LABELS: Record<ClinicalNeed, string> = {
  pt_ot: 'PT/OT',
  wound_care: 'Wound Care',
  iv_therapy: 'IV Therapy',
  medication_management: 'Medication Mgmt',
  skilled_nursing: 'Skilled Nursing',
  speech_therapy: 'Speech Therapy',
  cardiac_monitoring: 'Cardiac Monitoring',
};

export type UrgencyLevel = 'routine' | 'urgent' | 'emergent';

export type CoordinationStatus =
  | 'pending'
  | 'matching'
  | 'matched'
  | 'confirming'
  | 'confirmed'
  | 'confirm_failed'
  | 'awaiting_response'
  | 'negotiating'
  | 'accepted'
  | 'declined';

// ── Phase 8: 4-Track Workflow Types ──────────────────────────────────────────

export interface CoordinateNotification {
  id: string;
  patientId: string;
  patientName: string;
  message: string;
  createdAt: string;
}


export type InsuranceTrackStatus = 'not_checked' | 'checking' | 'authorized' | 'pending' | 'not_required' | 'issue';

export interface InsuranceTrack {
  status: InsuranceTrackStatus;
  notes?: string;
  authNumber?: string;
  checkedAt?: string;
  // Medicare home health documentation
  homeboundDocumented?: boolean;
  f2fEncounterComplete?: boolean;
  physicianCertification485?: 'not_started' | 'sent' | 'signed';
}

export type ProviderOutreachStatus =
  | 'calling'
  | 'responded_yes'
  | 'responded_no'
  | 'voicemail'
  | 'no_answer';

export interface ProviderOutreachAttempt {
  providerId: string;
  providerName: string;
  status: ProviderOutreachStatus;
  response?: string;
  attemptedAt: string;
  respondedAt?: string;
}

export interface ProviderOutreach {
  startedAt: string;
  attempts: ProviderOutreachAttempt[];
}

// ── Phase 7: Agent Layer Types ────────────────────────────────────────────────

export type AgentDraftType = 'provider_outreach' | 'patient_explanation' | 'family_update' | 'agency_narrative' | 'start_of_care_followup';

export interface AgentDraft {
  id: string;
  type: AgentDraftType;
  channel?: 'portal' | 'email';
  content: string;
  status: 'pending' | 'used' | 'dismissed';
  createdAt: string;
}

// ── Phase 6: Case Workspace Types ─────────────────────────────────────────────

export type ActivityEntryType = 'call' | 'note' | 'status_change' | 'document' | 'task' | 'agent';

export interface ActivityEntry {
  id: string;
  type: ActivityEntryType;
  timestamp: string;
  author: string;
  content: string;
  isAgent?: boolean;
}

export type TaskStatus = 'open' | 'in_progress' | 'done';
export type TaskPriority = 'low' | 'normal' | 'high';

export type MilestoneKey =
  | 'consent'
  | 'insurance_auth'
  | 'medicare_homebound'
  | 'medicare_f2f'
  | 'medicare_485'
  | 'provider_confirmed'
  | 'referral_sent'
  | 'start_of_care';

export interface CaseTask {
  id: string;
  title: string;
  notes?: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate?: string;
  assignee: string;
  createdAt: string;
  isSystemGenerated?: boolean;
  linkedBarrierId?: string;
  milestoneKey?: MilestoneKey;
}

export interface FamilyContact {
  id: string;
  name: string;
  relationship: string;
  phone: string;
  isDecisionMaker: boolean;
  notes?: string;
}

export interface PatientContext {
  preferredProviderNotes?: string;
  homeEnvironmentNotes?: string;
  transportationNotes?: string;
  consentDocumented: boolean;
  consentNotes?: string;
  consentTimestamp?: string;
  familyContacts: FamilyContact[];
  languageNeeds?: string;
}

export type BarrierType =
  | 'missing_doc'
  | 'payer_auth'
  | 'patient_preference'
  | 'clinical_complexity'
  | 'family_conflict'
  | 'capacity'
  | 'no_match';

export interface CaseBarrier {
  id: string;
  type: BarrierType;
  description: string;
  resolved: boolean;
  createdAt: string;
  resolvedAt?: string;
  linkedTaskId?: string;
}

export type NegotiationConditionStatus = 'pending' | 'met' | 'blocked';

export interface NegotiationCondition {
  id: string;
  description: string;
  status: NegotiationConditionStatus;
  notes?: string;
}

export interface ProviderNegotiation {
  providerName: string;
  startedAt: string;
  conditions: NegotiationCondition[];
  providerMessage?: string;
  resolvedAt?: string;
}

export interface CoordinateProvider {
  id: string;
  name: string;
  supportedDispositions: DispositionType[];
  services: ClinicalNeed[];
  acceptedInsurance: string[];
  coverageZips: string[];
  distanceMiles: number;
  acceptanceLikelihood: number;
  avgStartDays: number;
  qualityRating: number;
  healthSystemPreferred: boolean;
  currentCapacitySignal: 'high' | 'medium' | 'low';
}

export interface ProviderMatchScores {
  acceptanceLikelihood: number;
  timeToStart: number;
  clinicalFit: number;
  distance: number;
  quality: number;
  healthSystemPreference: number;
}

export interface ProviderMatch {
  provider: CoordinateProvider;
  totalScore: number;
  fitLabel: 'Best Fit' | 'Strong Option' | 'Backup';
  scores: ProviderMatchScores;
  passedHardFilters: boolean;
  whyRecommended: string[];
}

export interface ReferralDocument {
  id: string;
  title: string;
  type: 'discharge_summary' | 'pt_ot_notes' | 'wound_notes' | 'physician_orders' | 'insurance_auth' | 'agency_narrative';
  content: string;
  dateCreated: string;
}

export interface ReferralPacket {
  assembledAt: string;
  patient: {
    name: string;
    dob: string;
    address: string;
    phone: string;
  };
  insurance: {
    plan: string;
    memberId: string;
  };
  clinicalSummary: {
    diagnoses: string[];
    recentNotes: string;
    keyConditions: string[];
  };
  orders: string[];
  documents: ReferralDocument[];
}

export interface CoordinatePatient {
  id: string;
  name: string;
  age: number;
  dob: string;
  address: string;
  zip: string;
  phone: string;
  insurance: string;
  memberId: string;
  insuranceType: 'medicare' | 'medicare_advantage' | 'medicaid' | 'commercial';
  disposition: DispositionType;
  clinicalNeeds: ClinicalNeed[];
  urgency: UrgencyLevel;
  diagnoses: string[];
  recentNotes: string;
  coordinationStatus: CoordinationStatus;
  selectedProviderId?: string;
  referralPacket?: ReferralPacket;
  referralSentAt?: string;
  matchResults?: ProviderMatch[];
  lastMatchedAt?: string;
  predictProgram?: string;
  facilityName: string;
  assignedTo: string;
  // Phase 6: EHR-sourced fields
  admitDate: string;
  attendingPhysician: string;
  losTargetDays: number;
  languageNeeds?: string;
  // Phase 6: Case workspace state
  activityLog?: ActivityEntry[];
  tasks?: CaseTask[];
  patientContext?: PatientContext;
  barriers?: CaseBarrier[];
  providerNegotiation?: ProviderNegotiation;
  // Phase 7: Agent layer
  agentDrafts?: AgentDraft[];
  // Phase 8: 4-track workflow
  insuranceTrack?: InsuranceTrack;
  providerOutreach?: ProviderOutreach;
  startOfCareDate?: string;
}

export const STATUS_CHIP_STYLE: Record<CoordinationStatus, { label: string; bgcolor: string; color: string }> = {
  pending:            { label: 'Pending',           bgcolor: '#f6f6f6', color: '#668098' },
  matching:           { label: 'Matching…',         bgcolor: '#e5f6fb', color: '#153873' },
  matched:            { label: 'Destination Identified', bgcolor: '#ddf2f9', color: '#153873' },
  confirming:         { label: 'Confirming…',       bgcolor: '#f0eef8', color: '#635f99' },
  confirmed:          { label: 'Ready',     bgcolor: '#e0f7f4', color: '#006e60' },
  confirm_failed:     { label: 'Needs Attention',   bgcolor: '#fff3e0', color: '#e65100' },
  awaiting_response:  { label: 'Awaiting Response', bgcolor: '#e5f6fb', color: '#153873' },
  negotiating:        { label: 'Negotiating',       bgcolor: '#f0eef8', color: '#635f99' },
  accepted:           { label: 'Accepted',          bgcolor: '#e0f7f4', color: '#006e60' },
  declined:           { label: 'Declined',          bgcolor: '#ffebee', color: '#b71c1c' },
};

export const URGENCY_CHIP_STYLE: Record<UrgencyLevel, { label: string; bgcolor: string; color: string }> = {
  routine:  { label: 'Routine',  bgcolor: '#f6f6f6', color: '#668098' },
  urgent:   { label: 'Urgent',   bgcolor: '#fff3e0', color: '#e65100' },
  emergent: { label: 'Emergent', bgcolor: '#ffebee', color: '#b71c1c' },
};

export const URGENCY_BORDER_COLOR: Record<UrgencyLevel, string> = {
  routine:  'transparent',
  urgent:   'warning.main',
  emergent: 'error.main',
};

export interface CoordinateContextType {
  patients: CoordinatePatient[];
  providers: CoordinateProvider[];
  getPatientById: (id: string) => CoordinatePatient | undefined;
  runMatching: (patientId: string) => void;
  selectProvider: (patientId: string, providerId: string) => void;
  confirmProvider: (patientId: string) => void;
  sendReferral: (patientId: string) => void;
  simulateCapacityChange: (patientId: string) => void;
  updateDisposition: (patientId: string, disposition: DispositionType) => void;
  updateClinicalNeeds: (patientId: string, needs: ClinicalNeed[]) => void;
  updateUrgency: (patientId: string, urgency: UrgencyLevel) => void;
  simulateReferralResponse: (patientId: string, response: 'accepted' | 'declined' | 'negotiating') => void;
  predictIntegrationEnabled: boolean;
  setPredictIntegrationEnabled: (enabled: boolean) => void;
  // Phase 6: Case workspace actions
  addActivityEntry: (patientId: string, entry: Omit<ActivityEntry, 'id'>) => void;
  addTask: (patientId: string, task: Omit<CaseTask, 'id' | 'createdAt'>) => void;
  updateTask: (patientId: string, taskId: string, updates: Partial<CaseTask>) => void;
  updatePatientContext: (patientId: string, context: Partial<PatientContext>) => void;
  addBarrier: (patientId: string, barrier: Omit<CaseBarrier, 'id' | 'createdAt'>) => void;
  resolveBarrier: (patientId: string, barrierId: string) => void;
  updateNegotiationCondition: (patientId: string, conditionId: string, updates: Partial<NegotiationCondition>) => void;
  acceptNegotiation: (patientId: string) => void;
  // Phase 7: Agent layer actions
  useAgentDraft: (patientId: string, draftId: string) => void;
  editAgentDraft: (patientId: string, draftId: string, content: string) => void;
  dismissAgentDraft: (patientId: string, draftId: string) => void;
  // Phase 8: Provider outreach
  startProviderOutreach: (patientId: string) => void;
  selectProviderFromOutreach: (patientId: string, providerId: string) => void;
  checkInsuranceAuth: (patientId: string) => void;
  confirmStartOfCare: (patientId: string, date: string) => void;
  updateInsuranceDoc: (patientId: string, updates: Partial<InsuranceTrack>) => void;
  // Phase 8: Notifications
  notifications: CoordinateNotification[];
  dismissNotification: (id: string) => void;
}
